import { useCallback, useContext, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { ConfigContext } from '@/components/ConfigProvider';

/** 数据中心策略值支持 `a|b` 多值；请求侧需落在其中之一。 */
function dataCenterIn(rCluster: string, pCluster: string): boolean {
  if (typeof rCluster !== 'string' || typeof pCluster !== 'string') {
    return false;
  }
  const parts = pCluster.split('|');
  return parts.some((part) => part.trim() === rCluster);
}

/**
 * 与菜单 / Casbin 前端 enforcer 一致的策略匹配。
 * 注意：策略中的 `-` 是系统/空作用域的字面值，不是「匹配任意 datacenter/workspace」。
 */
export function matchCasbinPolicy(
  policy: string[],
  api: string,
  datacenter: string,
  workspace: string,
  group: string,
  namespace: string,
  resource: string,
  verb: string,
): boolean {
  const [pApi, pDatacenter, pWorkspace, pGroup, pNamespace, pResource, pVerb] = policy;

  return (
    (pApi === '*' || pApi === api) &&
    (pDatacenter === '*' || dataCenterIn(datacenter, pDatacenter)) &&
    (pWorkspace === '*' || pWorkspace === workspace) &&
    (pGroup === '*' || pGroup === group) &&
    (pNamespace === '*' || pNamespace === namespace) &&
    (pResource === '*' || pResource === resource) &&
    (pVerb === '*' || pVerb === verb)
  );
}

// 浏览器兼容的权限检查实现
interface SimpleEnforcer {
  enforceEx: (
    api: string,
    datacenter: string,
    workspace: string,
    group: string,
    namespace: string,
    resource: string,
    verb: string,
  ) => Promise<[boolean, string[] | undefined]>;
}

// 简化的权限检查实现
class BrowserCompatibleEnforcer implements SimpleEnforcer {
  private policies: string[][];

  constructor(policies: string[][]) {
    this.policies = policies;
  }

  async enforceEx(
    api: string,
    datacenter: string,
    workspace: string,
    group: string,
    namespace: string,
    resource: string,
    verb: string,
  ): Promise<[boolean, string[] | undefined]> {
    for (const policy of this.policies) {
      if (matchCasbinPolicy(policy, api, datacenter, workspace, group, namespace, resource, verb)) {
        return [true, undefined];
      }
    }
    return [false, ['No matching policy found']];
  }
}

// 使用 Map 缓存不同策略的 Enforcer 实例
const enforcerCache = new Map<string, SimpleEnforcer>();

function getEnforcer(policies: string[][]): SimpleEnforcer {
  const policyKey = JSON.stringify(policies); // 使用策略数组作为缓存键

  if (!enforcerCache.has(policyKey)) {
    // 如果该策略的 enforcer 实例不存在，则创建新的实例
    const enforcer = new BrowserCompatibleEnforcer(policies);
    enforcerCache.set(policyKey, enforcer);
  }
  // 返回对应策略的 Enforcer 实例
  return enforcerCache.get(policyKey)!;
}

/**
 * 执行 Casbin 权限校验，并返回详细的授权结果。
 *
 * @param policies 二维数组形式的策略规则，用于初始化 Casbin Enforcer。
 * @param api 请求的 API 域名，例如 'vpis vapi api apis'。
 * @param datacenter 请求的数据中心标识符 。
 * @param workspace 请求的工作空间标识符。
 * @param group 用户所属的用户组 'iam.stack.io''。
 * @param namespace 请求的命名空间。
 * @param resource 请求的资源名称，例如 'pods', 'events'。
 * @param verb 对资源的操作动词，例如 'get', 'list', 'watch'。
 * @returns 一个元组，包含两个元素：
 * - `okEx`: 布尔值，如果权限校验通过则为 `true`，否则为 `false`。
 * - `reason`: 如果权限校验失败，则返回包含失败原因的字符串数组；如果成功，则为 `undefined`。
 */
export async function enforceEx(
  policies: string[][],
  api: string,
  datacenter: string,
  workspace: string,
  group: string,
  namespace: string,
  resource: string,
  verb: string,
): Promise<[boolean, string[] | undefined]> {
  // 获取浏览器兼容的 Enforcer 实例
  const e = getEnforcer(policies);

  // 执行权限校验，将请求参数传递给 Enforcer
  // 会根据策略模型和规则，判断请求是否被允许
  const [okEx, reason] = await e.enforceEx(api, datacenter, workspace, group, namespace, resource, verb);

  // 返回校验结果和失败原因
  return [okEx, reason];
}

export function useAuthFun(group) {
  const { policies } = useContext(ConfigContext);

  const dom = useDom();

  const getAuthByVerb = useCallback(
    async (resource, action) => {
      // 从 dom 中提取 workspace 和 datacenter 信息
      let workspace = '-';
      let datacenter = '-';

      if (dom.module === 'tenant') {
        workspace = dom.workspace;
      } else if (dom.module === 'data-center') {
        datacenter = dom.cluster;
      }

      const [okEx] = await enforceEx(policies, '*', datacenter, workspace, group, '*', resource, action);

      return okEx;
    },
    [dom, policies, group],
  );

  return getAuthByVerb;
}

/** 按路由解析权限作用域；租户独立集群入口 /wsp 与 /console 使用相同工作空间。 */
export function useDom() {
  const { tenant, cluster } = useParams();
  const { pathname } = useLocation();

  // 查询参数变化会生成新的 params 对象；只依赖实际权限作用域，避免重新鉴权导致列表搜索草稿丢失。
  const dom = useMemo(() => {
    const [, v1] = pathname.split('/');
    if (tenant && (v1 === 'console' || v1 === 'wsp')) {
      // 控制台
      return {
        module: 'tenant',
        workspace: tenant,
        cluster: cluster,
      };
    } else if (cluster && (v1 === 'cluster' || v1 === 'data-center')) {
      //数据中心或集群
      return {
        module: 'data-center',
        cluster: cluster,
      };
    }
    return {
      module: 'system',
    };
  }, [tenant, cluster, pathname]);

  return dom;
}
